import { useCallback, useEffect, useRef, useState } from 'react'

type CommandType = 'RELOAD' | 'UPDATE_DATA' | 'NAVIGATE' | 'SHOW_NOTIFICATION' | 'TEST' | 'INIT_APPLY'

interface BaseCommand {
    type: CommandType
    id: number
    timestamp: string
}

interface ReloadCommand extends BaseCommand {
    type: 'RELOAD'
}

interface InitApplyCommand extends BaseCommand {
    type: 'INIT_APPLY'
}

interface TestCommand extends BaseCommand {
    type: 'TEST'
}

interface UpdateDataCommand<T = any> extends BaseCommand {
    type: 'UPDATE_DATA'
    data: T
}

interface NavigateCommand extends BaseCommand {
    type: 'NAVIGATE'
    path: string
}

interface ShowNotificationCommand extends BaseCommand {
    type: 'SHOW_NOTIFICATION'
    message: string
    variant?: 'success' | 'error' | 'warning' | 'info'
}

type BroadcastCommand =
    | ReloadCommand
    | UpdateDataCommand
    | NavigateCommand
    | ShowNotificationCommand
    | TestCommand
    | InitApplyCommand

function useBroadcastChannel(channelName: string) {
    const channelRef = useRef<BroadcastChannel | null>(null)
    const [messages, setMessages] = useState<BroadcastCommand[]>([])
    const [isReady, setIsReady] = useState(false)

    useEffect(() => {
        const channel = new BroadcastChannel(channelName)
        setMessages([])
        channelRef.current = channel
        setIsReady(true)

        channel.onmessage = (event: MessageEvent<BroadcastCommand>) => {
            setMessages((prev) => [...prev.slice(-99), event.data])
        }

        channel.onmessageerror = (error) => {
            console.error('BroadcastChannel error:', error)
            setIsReady(false)
        }

        return () => {
            setIsReady(false)
            channel.close()
            channelRef.current = null
        }
    }, [channelName])

    const sendCommand = useCallback(
        <T extends CommandType>(
            type: T,
            payload?: Omit<Extract<BroadcastCommand, { type: T }>, 'type' | 'id' | 'timestamp'>
        ) => {
            if (channelRef.current && isReady) {
                try {
                    const command = {
                        type,
                        id: Date.now(),
                        timestamp: new Date().toISOString(),
                        ...payload,
                    } as BroadcastCommand

                    channelRef.current.postMessage(command)
                } catch (error) {
                    console.error('Failed to send command:', error)
                }
            } else {
                console.warn('BroadcastChannel is not ready')
            }
        },
        [isReady]
    )

    const sendMessage = useCallback(
        (message: any) => {
            if (channelRef.current && isReady) {
                try {
                    channelRef.current.postMessage(message)
                } catch (error) {
                    console.error('Failed to send message:', error)
                }
            }
        },
        [isReady]
    )

    return { sendCommand, sendMessage, messages, isReady }
}

export default useBroadcastChannel
